import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { CheckCircle2, Loader2, CalendarClock, Phone } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const ConsultationModal = ({ isOpen, onClose, pkg = null }) => {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    clinicType: '',
    preferredTime: 'morning',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Prefill with the logged-in user's info each time the modal opens,
  // while staying editable for guests or if they want to enter someone else's details
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: currentUser?.name || '',
        phone: currentUser?.phone || '',
        clinicType: '',
        preferredTime: 'morning',
      });
    }
  }, [isOpen, currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const payload = {
        ...formData,
      };
      if (pkg?._id) {
        payload.packageId = pkg._id;
      }
      
      const response = await api.post('/clinic-inquiries', payload);
      if (response.data.success) {
        setIsSuccess(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setTimeout(() => {
      setFormData({
        name: '',
        phone: '',
        clinicType: '',
        preferredTime: 'morning',
      });
      setIsSuccess(false);
    }, 300);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && resetAndClose()}>
      <DialogContent className="sm:max-w-[425px]">
        {!isSuccess ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl text-[#1B365D] flex items-center gap-2">
                <CalendarClock className="w-5 h-5 text-secondary-600" />
                Book Consultation
              </DialogTitle>
              <DialogDescription>
                {pkg ? (
                  <>
                    Interested in the <span className="font-semibold text-gray-700">{pkg.name}</span>? 
                    Leave your details and our setup experts will call you back.
                  </>
                ) : (
                  "Leave your details below and our clinic setup experts will call you back to discuss your requirements."
                )}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div className="space-y-1.5">
                <label htmlFor="name" className="text-sm font-medium text-gray-700">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Dr. John Doe"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent transition-all"
                />
              </div>

              <div className="space-y-1.5">
                <label htmlFor="phone" className="text-sm font-medium text-gray-700">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <Phone className="w-4 h-4" />
                  </span>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+977 9800000000"
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label htmlFor="clinicType" className="text-sm font-medium text-gray-700">
                  Clinic Type
                </label>
                <select
                  id="clinicType"
                  name="clinicType"
                  value={formData.clinicType}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent bg-white transition-all"
                >
                  <option value="" disabled>Select type</option>
                  <option value="dental">Dental Clinic</option>
                  <option value="general">General Practice</option>
                  <option value="derma">Dermatology</option>
                  <option value="hospital">Small Hospital / Poly-clinic</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label htmlFor="preferredTime" className="text-sm font-medium text-gray-700">
                  Preferred Call Time
                </label>
                <select
                  id="preferredTime"
                  name="preferredTime"
                  value={formData.preferredTime}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent bg-white transition-all"
                >
                  <option value="morning">Morning (9 AM - 12 PM)</option>
                  <option value="afternoon">Afternoon (12 PM - 4 PM)</option>
                  <option value="evening">Evening (4 PM - 7 PM)</option>
                </select>
              </div>

              <div className="pt-3">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center py-2.5 px-4 bg-[#1B365D] text-white font-medium rounded-lg hover:bg-[#152a4a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1B365D] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Request Call Back"
                  )}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="py-8 px-4 text-center flex flex-col items-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Request Received!</h3>
            <p className="text-gray-600 mb-6 max-w-sm">
              Thank you for your interest. One of our clinic setup experts will contact you during your preferred time.
            </p>
            <button
              onClick={resetAndClose}
              className="w-full sm:w-auto px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConsultationModal;
